/**
 * Wave CM1 — browser helpers shared by the staff call dialog and the portal.
 *
 * Signaling is non-trickle: each side waits (briefly) for ICE gathering to
 * finish and sends one complete session description over HTTP.
 */

export type IceConfig = { iceServers: RTCIceServer[]; relay?: boolean };

export function webrtcSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof window.RTCPeerConnection === 'function'
    && Boolean(navigator.mediaDevices?.getUserMedia);
}

/** Resolve once ICE gathering completes, or after `timeoutMs` with whatever was gathered. */
export function waitForIceGathering(pc: RTCPeerConnection, timeoutMs = 4000): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', onChange);
      clearTimeout(timer);
      resolve();
    };
    const onChange = () => {
      if (pc.iceGatheringState === 'complete') done();
    };
    const timer = setTimeout(done, timeoutMs);
    pc.addEventListener('icegatheringstatechange', onChange);
  });
}

/** Microphone (+ camera when asked). Falls back to audio only if the camera is refused. */
export async function getCallMedia(wantVideo: boolean): Promise<{ stream: MediaStream; video: boolean }> {
  const audio: MediaTrackConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
  if (wantVideo) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio,
        video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 24 } },
      });
      return { stream, video: true };
    } catch {
      /* fall through to audio only */
    }
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio, video: false });
  return { stream, video: false };
}

export function mediaErrorMessage(err: unknown): string {
  const name = (err as { name?: string })?.name || '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Microphone access was blocked. Allow the microphone for this site in your browser and try again.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'No microphone was found on this device.';
  }
  if (name === 'NotReadableError') {
    return 'Your microphone is in use by another app. Close it and try again.';
  }
  return 'Could not start your microphone.';
}

export function stopStream(stream: MediaStream | null | undefined) {
  stream?.getTracks().forEach((t) => {
    try {
      t.stop();
    } catch {
      /* already stopped */
    }
  });
}

export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}` : `${m}:${String(r).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Recording (staff browser only, after the client consented)
// ---------------------------------------------------------------------------

const RECORDING_MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/mp4'];

export function pickRecordingMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of RECORDING_MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* ignore */
    }
  }
  return null;
}

type UploadPart = (blob: Blob, part: number) => Promise<void>;

/**
 * Records both sides of the call as one mixed audio track (no video — keeps
 * files small and avoids storing faces). Long calls are split into parts, each
 * a complete playable file: when a part grows past `partBytes` the recorder is
 * stopped and a fresh one started on the same mixed stream.
 */
export class CallRecorder {
  private ctx: AudioContext;
  private dest: MediaStreamAudioDestinationNode;
  private sources: MediaStreamAudioSourceNode[] = [];
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private bytes = 0;
  private part = 0;
  private stopping = false;
  private pending: Promise<void>[] = [];
  readonly mimeType: string;

  constructor(
    private upload: UploadPart,
    private partBytes: number,
    mimeType: string,
    private onError: (message: string) => void,
  ) {
    const Ctx: typeof AudioContext = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.dest = this.ctx.createMediaStreamDestination();
    this.mimeType = mimeType;
  }

  addStream(stream: MediaStream) {
    if (!stream.getAudioTracks().length) return;
    const src = this.ctx.createMediaStreamSource(stream);
    src.connect(this.dest);
    this.sources.push(src);
  }

  async start() {
    if (this.ctx.state === 'suspended') await this.ctx.resume().catch(() => undefined);
    this.startPart();
  }

  get partsStarted() {
    return this.part;
  }

  private startPart() {
    this.part += 1;
    const partNo = this.part;
    this.chunks = [];
    this.bytes = 0;
    const rec = new MediaRecorder(this.dest.stream, { mimeType: this.mimeType, audioBitsPerSecond: 24000 });
    this.recorder = rec;
    const chunks = this.chunks;
    rec.ondataavailable = (e) => {
      if (!e.data || !e.data.size) return;
      chunks.push(e.data);
      this.bytes += e.data.size;
      if (!this.stopping && this.bytes >= this.partBytes && rec.state === 'recording') {
        rec.stop();
      }
    };
    rec.onstop = () => {
      const blob = new Blob(chunks, { type: this.mimeType.split(';')[0] });
      if (blob.size > 0) {
        this.pending.push(this.upload(blob, partNo).catch((err: Error) => this.onError(err?.message || 'Recording upload failed')));
      }
      if (!this.stopping) this.startPart();
    };
    rec.start(5000);
  }

  /** Stop recording and wait until every part has been uploaded. */
  async stop(): Promise<void> {
    this.stopping = true;
    const rec = this.recorder;
    if (rec && rec.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        const prev = rec.onstop;
        rec.onstop = (ev) => {
          if (prev) (prev as (this: MediaRecorder, ev: Event) => void).call(rec, ev);
          resolve();
        };
        try {
          rec.stop();
        } catch {
          resolve();
        }
      });
    } else {
      // A part may have just auto-stopped; give its onstop a moment to queue the upload.
      await new Promise((r) => setTimeout(r, 150));
    }
    await Promise.all(this.pending);
    this.sources.forEach((s) => {
      try {
        s.disconnect();
      } catch {
        /* ignore */
      }
    });
    await this.ctx.close().catch(() => undefined);
  }
}

// ---------------------------------------------------------------------------
// Ring tone (portal) — short beeps generated in the browser, no audio file.
// ---------------------------------------------------------------------------

export class RingTone {
  private ctx: AudioContext | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  start() {
    if (this.timer) return;
    try {
      const Ctx: typeof AudioContext = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctx();
    } catch {
      return;
    }
    const beep = () => {
      const ctx = this.ctx;
      if (!ctx || ctx.state === 'closed') return;
      [0, 0.25].forEach((offset) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = 440;
        gain.gain.value = 0.08;
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + offset);
        osc.stop(ctx.currentTime + offset + 0.18);
      });
    };
    beep();
    this.timer = setInterval(beep, 2500);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }
}
